import type { ReactNode } from "react";
import { type PersonalItem } from "../../lib/libraries";

export function formatPreviewValue(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return `${val}`;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).slice(0, 3).join(', ');
  try { return JSON.stringify(val); } catch { return String(val); }
}

export function renderPersonalItemPreview(item: PersonalItem): ReactNode {
  const data = item?.data;
  if (data == null) return null;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed ? <p className="mt-2 text-xs text-fg-2 whitespace-pre-wrap break-words">{trimmed}</p> : null;
  }
  if (typeof data !== 'object') return null;
  const safe = data as Record<string, unknown>;

  if (item.type === 'link') {
    const url = typeof safe.url === 'string' ? safe.url : (typeof safe.href === 'string' ? safe.href : '');
    const summary = formatPreviewValue(safe.summary || safe.note || safe.description);
    const hostname = (() => {
      if (!url) return '';
      try {
        const u = new URL(url.startsWith('http') ? url : `https://${url}`);
        return u.hostname.replace(/^www\./, '');
      } catch { return ''; }
    })();
    return (
      <div className="mt-2 space-y-1 text-xs text-fg-2">
        {url && (
          <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-sm font-medium">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.07 0l1.83-1.83a5 5 0 0 0-7.07-7.07L10 5"/><path d="M14 11a5 5 0 0 0-7.07 0L5.1 12.83a5 5 0 0 0 7.07 7.07L14 19"/></svg>
            <span className="truncate">{hostname || url}</span>
          </a>
        )}
        {summary && <p className="text-fg-2">{summary}</p>}
      </div>
    );
  }

  if (item.type === 'note') {
    const body = formatPreviewValue(safe.body || safe.content || safe.text);
    return body ? <p className="mt-2 text-xs text-fg-2 whitespace-pre-wrap break-words">{body}</p> : null;
  }

  if (item.type === 'task') {
    const due = formatPreviewValue(safe.due || safe.dueDate);
    const status = formatPreviewValue(safe.status || safe.state);
    const priority = formatPreviewValue(safe.priority);
    return (
      <div className="mt-2 space-y-1 text-xs text-fg-2">
        {status && <div><span className="text-fg-3">Status:</span> {status}</div>}
        {priority && <div><span className="text-fg-3">Priority:</span> {priority}</div>}
        {due && <div><span className="text-fg-3">Due:</span> {due}</div>}
      </div>
    );
  }

  const entries = Object.entries(safe).filter(([, value]) => value != null && value !== '').slice(0, 4);
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-1 text-xs text-fg-2">
      {entries.map(([key, value]) => (
        <div key={key}>
          <div className="text-meta text-fg-3">{key}</div>
          <div className="text-fg-2 truncate">{formatPreviewValue(value)}</div>
        </div>
      ))}
      {Object.keys(safe).length > entries.length && (
        <div className="text-meta text-fg-3">
          +{Object.keys(safe).length - entries.length} more {Object.keys(safe).length - entries.length === 1 ? 'field' : 'fields'}
        </div>
      )}
    </div>
  );
}
